import { getRandomIntInclusive } from '@application/utils';
import { Avatar, SkeletonBox } from '@presentation/components';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { TbChevronRight } from 'react-icons/tb';
import * as RootStyled from '../../styled';

export const PageSkeleton: FC = () => {
  const { translate } = useTranslation();

  const skeletons = useMemo(() => {
    return {
      title: { width: getRandomIntInclusive(40, 70), height: 21.5 },
      currentConversation: {
        id: 1,
        name: { width: getRandomIntInclusive(40, 70), height: 21.5 },
      },
      conversations: Array(getRandomIntInclusive(4, 6))
        .fill(undefined)
        .map((_, index) => ({
          id: index,
          hasUnseenMessages: false,
          name: { width: getRandomIntInclusive(40, 70), height: 21.5 },
        })),
      messageSections: Array(getRandomIntInclusive(2, 3))
        .fill(undefined)
        .map(() => ({
          title: { width: getRandomIntInclusive(40, 70), height: 21.5 },
          messages: Array(getRandomIntInclusive(4, 6))
            .fill(undefined)
            .map((_, index) => ({
              id: index,
              isOwn: index % 2 === 0,
              text: { width: getRandomIntInclusive(150, 270), height: 21.5 },
              time: null,
            })),
        })),
    };
  }, []);

  const skeletonSideSection = useCallback(() => {
    return skeletons.conversations.map((conversation, index) => {
      return (
        <RootStyled.UserWrapper
          key={index}
          active={conversation.id === skeletons.currentConversation.id}
        >
          <RootStyled.UserTextWrapper>
            {conversation.hasUnseenMessages && <RootStyled.NotificationDot />}
            <Avatar size={36} src={PLACEHOLDER_IMAGE_USER} />
            <RootStyled.UserName>
              <SkeletonBox style={conversation.name} />
            </RootStyled.UserName>
            <RootStyled.ConversationForwardButton>
              <TbChevronRight />
            </RootStyled.ConversationForwardButton>
          </RootStyled.UserTextWrapper>
        </RootStyled.UserWrapper>
      );
    });
  }, [skeletons.conversations, skeletons.currentConversation.id]);

  const skeletonMessages = useCallback(
    () =>
      skeletons.messageSections.map((messageSection, index) => (
        <RootStyled.MessageSection key={index}>
          <RootStyled.MessageSectionTitle isToday="false">
            <SkeletonBox style={messageSection.title} />
          </RootStyled.MessageSectionTitle>

          <RootStyled.MessagesWrapper>
            {messageSection.messages.map((message) => (
              <RootStyled.MessageWrapper key={message.id} ownMessage={message.isOwn}>
                <RootStyled.MessageBox ownMessage={message.isOwn}>
                  <RootStyled.MessageContentWrapper ownMessage={message.isOwn}>
                    <RootStyled.MessageText ownMessage={message.isOwn}>
                      <SkeletonBox style={message.text} />
                    </RootStyled.MessageText>
                  </RootStyled.MessageContentWrapper>
                </RootStyled.MessageBox>
              </RootStyled.MessageWrapper>
            ))}
          </RootStyled.MessagesWrapper>
        </RootStyled.MessageSection>
      )),
    [skeletons.messageSections],
  );

  return (
    <RootStyled.Wrapper>
      <RootStyled.SideWrapper mobileMessagesShown={false}>
        <RootStyled.Title>{translate('mt.messages.title')}</RootStyled.Title>

        {skeletonSideSection()}
      </RootStyled.SideWrapper>
      <RootStyled.ContentWrapper mobileMessagesShown={false}>
        <RootStyled.TopWrapper>
          <RootStyled.ConversationForwardButton onClick={undefined}>
            <TbChevronRight />
          </RootStyled.ConversationForwardButton>
          <Avatar size={36} src={PLACEHOLDER_IMAGE_USER} />
          <RootStyled.TopUserName>
            <SkeletonBox style={skeletons.currentConversation.name} />
          </RootStyled.TopUserName>
        </RootStyled.TopWrapper>
        <RootStyled.MessageSectionsWrapper id="mt-message-box">
          {skeletonMessages()}
        </RootStyled.MessageSectionsWrapper>
      </RootStyled.ContentWrapper>
    </RootStyled.Wrapper>
  );
};
